import { useEffect, useState } from 'react';
import { LAYER_POSITIONS, LAYER_STYLES, LAYER_FONTS, type LayerPosition, type LayerStyle, type LayerFont, type LayerProps } from '../lib/api';
import type { StoredLayer } from '../lib/timeline';
import { PicturePicker } from './PicturePicker';

/**
 * A banner, edited where it is.
 *
 * Thomas: "how can we edit or expand the text". A banner could be placed
 * and removed and nothing else — lengthening one meant deleting it and
 * typing it again. This is the banner's own inspector: its words, its
 * corner, when it starts on its scene and how long it stays.
 */
export function LayerInspector({ layer, sceneIndex, busy, onSave, onRemove, onClose, onSaveElement, cardId, logos, pictureKey }: {
  layer: StoredLayer;
  /** F15: the card and the show's logos, for a lower third's picture cell. */
  cardId?: string;
  logos?: string[] | null;
  pictureKey?: string;
  /** Which scene it hangs from, for the label. */
  sceneIndex: number | null;
  busy: boolean;
  onSave: (patch: { offsetSeconds: number; seconds: number; props: LayerProps }) => void;
  onRemove: () => void;
  onClose: () => void;
  /** E1: keep this layer as one of the show's elements, for every episode. */
  onSaveElement?: (label: string, props: LayerProps, seconds: number) => void;
}) {
  const [text, setText] = useState(layer.props?.text ?? '');
  const [position, setPosition] = useState<LayerPosition>((layer.props?.position as LayerPosition) ?? 'bottom');
  const [offset, setOffset] = useState(Number(layer.offset_s) || 0);
  const [seconds, setSeconds] = useState(Number(layer.seconds) || 1);
  // How it is set — like text. Defaults are the renderer's, so an untouched
  // banner reads exactly as it did before any of this existed.
  const p0 = layer.props ?? {};
  const [style, setStyle] = useState<LayerStyle>((p0.style as LayerStyle) ?? 'banner');
  const [size, setSize] = useState<number>(p0.size ?? (p0.style === 'bug' ? 22 : 38));
  const [font, setFont] = useState<LayerFont>((p0.font as LayerFont) ?? 'sans');
  const [bold, setBold] = useState<boolean>(p0.bold ?? (p0.style !== 'bug'));
  const [italic, setItalic] = useState<boolean>(Boolean(p0.italic));
  const [underline, setUnderline] = useState<boolean>(Boolean(p0.underline));
  const [color, setColor] = useState<string>(p0.color ?? '');
  const [sub, setSub] = useState<string>(p0.sub ?? '');
  const [url, setUrl] = useState<string>(p0.url ?? '');
  const [cell, setCell] = useState<string>(p0.style === 'lower-third' ? (p0.assetId ?? '') : '');
  const [height, setHeight] = useState<number>(p0.height ?? 120);
  // A sound cue (086): a level under the speech and its fades, nothing drawn.
  const [gainDb, setGainDb] = useState<number>(p0.gainDb ?? -6);
  const [fadeIn, setFadeIn] = useState<number>(p0.fadeIn ?? 0);
  const [fadeOut, setFadeOut] = useState<number>(p0.fadeOut ?? 0);
  const picture = layer.track === 'image';
  const sound = layer.track === 'sound';
  useEffect(() => {
    const p = layer.props ?? {};
    setText(p.text ?? '');
    setPosition((p.position as LayerPosition) ?? 'bottom');
    setOffset(Number(layer.offset_s) || 0);
    setSeconds(Number(layer.seconds) || 1);
    setStyle((p.style as LayerStyle) ?? 'banner');
    setSize(p.size ?? (p.style === 'bug' ? 22 : 38));
    setFont((p.font as LayerFont) ?? 'sans');
    setBold(p.bold ?? (p.style !== 'bug'));
    setItalic(Boolean(p.italic)); setUnderline(Boolean(p.underline)); setColor(p.color ?? '');
    setSub(p.sub ?? ''); setUrl(p.url ?? ''); setHeight(p.height ?? 120);
    setCell(p.style === 'lower-third' ? (p.assetId ?? '') : '');
    setGainDb(p.gainDb ?? -6); setFadeIn(p.fadeIn ?? 0); setFadeOut(p.fadeOut ?? 0);
  }, [layer.id, layer.props, layer.offset_s, layer.seconds]);

  const props: LayerProps = sound
    ? { assetId: p0.assetId, gainDb, fadeIn, fadeOut }
    : picture
    ? { assetId: p0.assetId, position, height }
    : {
        text: text.trim(), position, style, size, font, bold, italic, underline,
        ...(color ? { color } : {}),
        ...(style === 'lower-third' && sub.trim() ? { sub: sub.trim() } : {}),
        ...(style === 'lower-third' && /^https?:\/\//.test(url.trim()) ? { url: url.trim() } : {}),
        ...(style === 'lower-third' && cell ? { assetId: cell } : {}),
      };
  const was: LayerProps = sound
    ? { assetId: p0.assetId, gainDb: p0.gainDb ?? -6, fadeIn: p0.fadeIn ?? 0, fadeOut: p0.fadeOut ?? 0 }
    : picture
    ? { assetId: p0.assetId, position: (p0.position as LayerPosition) ?? 'bottom', height: p0.height ?? 120 }
    : {
        text: (p0.text ?? '').trim(), position: (p0.position as LayerPosition) ?? 'bottom',
        style: (p0.style as LayerStyle) ?? 'banner', size: p0.size ?? (p0.style === 'bug' ? 22 : 38),
        font: (p0.font as LayerFont) ?? 'sans', bold: p0.bold ?? (p0.style !== 'bug'),
        italic: Boolean(p0.italic), underline: Boolean(p0.underline), ...(p0.color ? { color: p0.color } : {}),
        ...(p0.style === 'lower-third' && p0.sub?.trim() ? { sub: p0.sub.trim() } : {}),
        ...(p0.style === 'lower-third' && p0.url?.trim() ? { url: p0.url.trim() } : {}),
        ...(p0.style === 'lower-third' && p0.assetId ? { assetId: p0.assetId } : {}),
      };
  const changed = JSON.stringify(props) !== JSON.stringify(was)
    || offset !== (Number(layer.offset_s) || 0) || seconds !== (Number(layer.seconds) || 1);

  return (
    <div className="lyi" data-tool="layer">
      <div className="row between">
        <span className="lbl-mono">{sound ? 'Cue' : picture ? 'Picture' : 'Banner'}{sceneIndex !== null ? ` · on scene ${sceneIndex + 1}` : ''}</span>
        <button className="btn tiny" onClick={onClose} aria-label="Close banner editor">×</button>
      </div>
      {sound && (
        <div className="lyi-row">
          <label className="field">
            Level
            <input type="number" min={-60} max={0} step={1} value={gainDb}
                   title="dB under the speech. −6 is a stinger that lands; −18 sits with the bed."
                   onChange={(e) => setGainDb(Math.max(-60, Math.min(0, Math.round(Number(e.target.value) || 0))))} />
          </label>
          <label className="field">
            Fade in (s)
            <input type="number" min={0} max={30} step={0.1} value={fadeIn}
                   onChange={(e) => setFadeIn(Math.max(0, Math.min(30, Number(e.target.value) || 0)))} />
          </label>
          <label className="field">
            Fade out (s)
            <input type="number" min={0} max={30} step={0.1} value={fadeOut}
                   onChange={(e) => setFadeOut(Math.max(0, Math.min(30, Number(e.target.value) || 0)))} />
          </label>
        </div>
      )}
      {picture ? (
        <div className="lyi-row">
          <label className="field">
            Height
            <input type="number" min={40} max={400} step={10} value={height}
                   title="Pixels tall on a 1920x1080 frame"
                   onChange={(e) => setHeight(Math.max(40, Math.min(400, Math.round(Number(e.target.value) || 40))))} />
          </label>
          <label className="field">
            Corner
            <select value={position} onChange={(e) => setPosition(e.target.value as LayerPosition)}>
              {LAYER_POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          <span />
        </div>
      ) : null}
      {!picture && !sound && (
      <label className="field">
        Text
        <textarea rows={2} maxLength={200} value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      )}
      {!picture && !sound && style === 'lower-third' && (
        <label className="field">
          Second line
          <input maxLength={120} value={sub} placeholder="Their role, the subject, the source"
                 onChange={(e) => setSub(e.target.value)} />
        </label>
      )}
      {!picture && style === 'lower-third' && (
        <label className="field">
          Picture cell
          <span className="hint">A picture on the band's left — the app's icon, the show's mark. Click it again to take it off.</span>
          <PicturePicker cardId={cardId} logos={logos} value={cell ? [cell] : []} max={1} tool="cell-picture" refreshKey={pictureKey}
                         onChange={(ids) => setCell(ids[0] ?? '')} />
        </label>
      )}
      {!picture && style === 'lower-third' && (
        <label className="field">
          Link as a QR code
          <input maxLength={200} value={url} placeholder="https://… — a code the viewer's phone reads, in the band's right cell"
                 onChange={(e) => setUrl(e.target.value)} />
        </label>
      )}
      {!picture && !sound && (
      <div className="lyi-row">
        <label className="field">
          Style
          <select value={style} title={LAYER_STYLES.find((x) => x.id === style)?.hint}
                  onChange={(e) => {
                    const next = e.target.value as LayerStyle;
                    setStyle(next);
                    // A bug is small and light unless the creator says otherwise.
                    if (next === 'bug' && size === 38) { setSize(22); setBold(false); }
                    if (next !== 'bug' && size === 22) { setSize(38); setBold(true); }
                  }}>
            {LAYER_STYLES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <label className="field">
          Face
          <select value={font} onChange={(e) => setFont(e.target.value as LayerFont)}>
            {LAYER_FONTS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <label className="field">
          Size
          <input type="number" min={16} max={120} step={2} value={size}
                 onChange={(e) => setSize(Math.max(16, Math.min(120, Math.round(Number(e.target.value) || 16))))} />
        </label>
      </div>
      )}
      {!picture && !sound && (
      <div className="lyi-row lyi-type">
        <span className="lyi-toggles" role="group" aria-label="Weight and style">
          <button type="button" className={`btn tiny${bold ? ' is-on' : ''}`} aria-pressed={bold}
                  title="Bold" onClick={() => setBold(!bold)}><b>B</b></button>
          <button type="button" className={`btn tiny${italic ? ' is-on' : ''}`} aria-pressed={italic}
                  title="Italic" onClick={() => setItalic(!italic)}><i>I</i></button>
          <button type="button" className={`btn tiny${underline ? ' is-on' : ''}`} aria-pressed={underline}
                  title="Underline" onClick={() => setUnderline(!underline)}><u>U</u></button>
        </span>
        <label className="field">
          Colour
          <span className="row" style={{ gap: 6 }}>
            <input type="color" value={color || '#ffffff'} title="Text colour"
                   onChange={(e) => setColor(e.target.value)} />
            <button type="button" className="btn tiny" title="Back to the kit's ink"
                    disabled={!color} onClick={() => setColor('')}>kit</button>
          </span>
        </label>
        <span />
      </div>
      )}
      <div className="lyi-row">
        {!picture && !sound ? (
        <label className="field">
          Corner
          <select value={position} onChange={(e) => setPosition(e.target.value as LayerPosition)}>
            {LAYER_POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        ) : <span />}
        <label className="field">
          Starts at
          <input type="number" min={0} step={0.5} value={offset}
                 title="Seconds after the scene begins"
                 onChange={(e) => setOffset(Math.max(0, Number(e.target.value) || 0))} />
        </label>
        <label className="field">
          Lasts
          <input type="number" min={0.5} step={0.5} value={seconds}
                 title={sound ? 'Seconds it plays — cut there if the file is longer' : 'Seconds on screen'}
                 onChange={(e) => setSeconds(Math.max(0.5, Number(e.target.value) || 0.5))} />
        </label>
      </div>
      <div className="row">
        <button className="btn pri" disabled={busy || !changed || (!picture && !sound && !text.trim())}
                onClick={() => onSave({ offsetSeconds: offset, seconds, props })}>
          {busy ? 'Saving…' : sound ? 'Save cue' : picture ? 'Save picture' : 'Save banner'}
        </button>
        <button className="btn" disabled={busy} onClick={onRemove}>Remove</button>
        {onSaveElement && !picture && (
          <button className="btn" disabled={busy || !text.trim()}
                  title="Keep this banner, button, bug or lower third with the show, to place on any episode from + tag"
                  onClick={() => onSaveElement(text.trim().slice(0, 60), props, seconds)}>
            Save to the show's elements
          </button>
        )}
      </div>
    </div>
  );
}
